import { useId, useRef, useState } from 'react'
import { Button } from './Button'
import { cx } from './cx'
import s from './CopyField.module.css'

export interface CopyFieldProps {
  /** What the value IS ("Connector address"). Names the group and the button. */
  label: string
  /** The value a person copies — shown verbatim, never truncated. */
  value: string
  /** Root test id; the parts are `<testId>-value`, `-copy` and `-status`. */
  testId?: string
  className?: string
  /** Optional governed wording for hosts whose visible copy is localized. */
  copyLabel?: string
  copiedMessage?: string
  copyFailedMessage?: string
}

type CopyState = 'idle' | 'copied' | 'failed'

const SAID: Record<Exclude<CopyState, 'idle'>, string> = {
  copied: 'Copied',
  failed: 'Could not copy — select it and copy it manually',
}

/**
 * A VALUE TO COPY (#2512): a read-only value in the value-slot material and
 * one raised "Copy" block beside it.
 *
 * ── why this is in the kit ──────────────────────────────────────────────────
 * The Console's and the Portal's Connected apps both hand a person the same
 * connector address to paste into another product, and the portal had drawn
 * its own `<code>` + button row with a private stylesheet. Two hosts, one
 * thing: the kit owns it (the shape of `SearchField` and `CountPill`).
 *
 * ── what it promises ────────────────────────────────────────────────────────
 *   · the VALUE is text in the READ-ONLY slot (recess kept, lip lost —
 *     `FieldEditor`'s `.readOnlyValue` material), in the brand's mono face,
 *     and it WRAPS anywhere: a long URL never pushes the page sideways at
 *     320px. It is ordinary selectable text, so the manual fallback works;
 *   · Copy is the kit `Button`. It is never disabled while copying, so focus
 *     stays on it (a disabled button drops focus to the document);
 *   · the outcome is spoken in a POLITE live region that exists before it is
 *     filled — "Copied", or "Could not copy — select it and copy it
 *     manually" when the clipboard is missing or refuses. The region is
 *     cleared before each attempt, so a second success is announced again;
 *   · the group is named by `label`, and the button by "Copy <label>", so two
 *     copy fields on one page are two distinct controls.
 */
export function CopyField({
  label, value, testId, className, copyLabel = 'Copy', copiedMessage = SAID.copied,
  copyFailedMessage = SAID.failed,
}: CopyFieldProps) {
  const labelId = `copy-field-${useId().replace(/:/g, '')}`
  const [state, setState] = useState<CopyState>('idle')
  /* Only the latest press may settle the region: a slow first write that
     resolves after a second press must not overwrite the second's answer. */
  const attempt = useRef(0)

  async function copy() {
    const mine = ++attempt.current
    setState('idle')
    let ok = false
    try {
      const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard
      if (clipboard && typeof clipboard.writeText === 'function') {
        await clipboard.writeText(value)
        ok = true
      }
    } catch {
      ok = false
    }
    if (attempt.current === mine) setState(ok ? 'copied' : 'failed')
  }

  const part = (name: string) => (testId ? `${testId}-${name}` : undefined)

  return (
    <div className={cx(s.field, className)} role="group" aria-labelledby={labelId}
      data-copy-field="" data-testid={testId}>
      <span className={s.label} id={labelId}>{label}</span>
      <div className={s.row}>
        <span className={s.value} data-testid={part('value')}>{value}</span>
        <Button type="button" variant="secondary" className={s.copy} aria-label={`${copyLabel} ${label}`}
          onClick={() => { void copy() }} data-testid={part('copy')}>{copyLabel}</Button>
        <span className={s.status} role="status" aria-live="polite" data-state={state}
          data-testid={part('status')}>{state === 'idle' ? '' : state === 'copied' ? copiedMessage : copyFailedMessage}</span>
      </div>
    </div>
  )
}
